import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/Stack";
import { Banner } from "@astryxdesign/core/Banner";
import type { PublicBooking } from "@/lib/api/schemas";
import { buildIcs, downloadIcs } from "@/lib/ics";
import { pushAvailability, pushReason, subscribePush, unsubscribePush } from "@/lib/pwa";
import { publicApi } from "@/lib/api/public";
import { errorMessage } from "@/lib/errors";
import { useT } from "@/lib/i18n";

/**
 * Two channels with honest states:
 *  - calendar file: we can only hand over a file; we never claim it was added;
 *  - push: shows exactly what this device supports (iPhone needs Home Screen install).
 */
export function NotifyAndCalendar({ booking, token, slug }: { booking: PublicBooking; token: string; slug: string }) {
  const t = useT();
  const [icsSaved, setIcsSaved] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const push = useQuery({ queryKey: ["push-state", slug], queryFn: () => pushAvailability(slug), staleTime: 0 });
  const [busy, setBusy] = useState(false);

  const saveIcs = () => {
    downloadIcs(
      `zapis-${booking.starts_at.slice(0, 10)}.ics`,
      buildIcs({
        uid: `${booking.id}-v${booking.version}@studio-booking`,
        title: `${booking.service_name} — ${booking.tenant.name}`,
        description: t.notify.icsDescription(location.href),
        location: booking.tenant.address ?? undefined,
        startsAt: booking.starts_at,
        endsAt: booking.ends_at,
        url: location.href,
      }),
    );
    setIcsSaved(true);
  };

  const enablePush = async () => {
    setBusy(true);
    setPushError(null);
    try {
      const sub = await subscribePush(slug);
      await publicApi.savePush(token, sub);
    } catch (e) {
      setPushError((e as Error).message === "permission_denied" ? t.notify.permissionDenied : errorMessage(e));
    } finally {
      setBusy(false);
      void push.refetch();
    }
  };

  const disablePush = async () => {
    setBusy(true);
    try {
      const endpoint = await unsubscribePush(slug);
      if (endpoint) await publicApi.deletePush(token, endpoint);
    } catch (e) {
      setPushError(errorMessage(e));
    } finally {
      setBusy(false);
      void push.refetch();
    }
  };

  const state = push.data;
  return (
    <VStack gap={3}>
      <VStack gap={1}>
        <Button label={t.notify.ics} variant="secondary" width="100%" onClick={saveIcs} />
        <Text type="supporting" aria-live="polite">
          {icsSaved ? t.notify.icsSaved : t.notify.icsHint}
        </Text>
      </VStack>

      <VStack gap={1}>
        {state?.state === "subscribed" ? (
          <HStack gap={2} vAlign="center" wrap="wrap">
            <Text type="body">{t.notify.pushOn}</Text>
            <Button label={t.notify.disable} variant="ghost" size="sm" isLoading={busy} onClick={() => void disablePush()} />
          </HStack>
        ) : state?.state === "available" ? (
          <Button label={t.notify.enable} variant="secondary" width="100%" isLoading={busy} onClick={() => void enablePush()} />
        ) : state ? (
          <Text type="supporting">{pushReason(state.state)}</Text>
        ) : null}
        {booking.is_demo && state?.state === "subscribed" ? (
          <Text type="supporting">{t.notify.demo}</Text>
        ) : null}
        {pushError ? <Banner status="warning" title={t.notify.failedTitle} description={pushError} collapsible={false} /> : null}
      </VStack>
    </VStack>
  );
}
